import {
  ChatOutlined,
  ClearAllOutlined,
  CloudDownload,
  CloudSync,
  EditOutlined,
  FileUploadOutlined,
  PersonAddAlt1Outlined,
  SyncLock,
  SyncProblem,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Can, usePermission } from "src/helpers/Auth";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { ClearDatasetModal } from "src/views/forms/DatasetModals";
import { UpdateDatasetModal } from "src/views/forms/datasetModels/UpdateDatasetModal";
import ShareDialog from "src/reusable/components/ShareDialog";
import DatasetDownloadAllButton from "../components/DatasetDownloadAllButton";
import { useSyncRemoteDataset } from "../services/DatasetServices";
import { Dataset } from "../types";
import CollectionFlagToggle from "./CollectionFlagToggle";

const DatasetHeaderActions = ({
  dataset,
  overrideDatasetId,
  hideElements,
}: {
  dataset: Dataset;
  overrideDatasetId?: string;
  hideElements?: {
    header?: boolean;
    uploadButton?: boolean;
    sideDrawer?: boolean;
    downloadButton?: boolean;
  };
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const [isClearDatasetModalOpen, setIsClearDatasetModalOpen] = useState(false);
  const [isUpdateDatasetModalOpen, setisUpdateDatasetModalOpen] =
    useState(false);
  const [isManageAccessOpen, setIsManageAccessOpen] = useState(false);

  // Owner can manage access regardless of the dataset:update RBAC role (backend
  // enforces per-object manage); others need the role.
  const currentUser = useGetUserDetails();
  const isOwner = !!currentUser && dataset?.create_user_fk === currentUser.id;
  // Ownership transfer is owner-or-admin (the backend enforces the same).
  const { canIt: isAdmin } = usePermission("admin");

  // Per-dataset ACL: `Can perform="dataset_data:write"` only proves the ROLE
  // may write dataset data somewhere; whether this user may write THIS
  // dataset (owner / write grant) is server-decided and rides on the dataset
  // payload as my_access. Explicit-deny semantics: only a present-and-false
  // flag disables the buttons (a payload without flags must not false-
  // disable) — the upload page itself enforces the strict check.
  const canWriteDataset = dataset?.my_access?.can_write !== false;
  // Same explicit-deny semantics for per-object manage: a server-confirmed
  // can_manage=false renders the disabled variant instead of a button that
  // would 403 on request.
  const canManageDataset = dataset?.my_access?.can_manage !== false;

  const manageAccessDeniedButton = (
    <Tooltip title="You do not have permission to manage access to this dataset">
      <span>
        {isMobile ? (
          <IconButton
            data-testid={`sdr-btn-manage-access-${dataset.id}`}
            size="small"
            disabled
          >
            <PersonAddAlt1Outlined />
          </IconButton>
        ) : (
          <Button
            data-testid={`sdr-btn-manage-access-${dataset.id}`}
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<PersonAddAlt1Outlined />}
            disabled
          >
            Manage access
          </Button>
        )}
      </span>
    </Tooltip>
  );

  const manageAccessButton = isMobile ? (
    <Tooltip title="Manage access">
      <IconButton
        data-testid={`sdr-btn-manage-access-${dataset.id}`}
        data-attr={`datasets:btn:manage-access-${dataset.id}`}
        size="small"
        onClick={() => setIsManageAccessOpen(true)}
      >
        <PersonAddAlt1Outlined />
      </IconButton>
    </Tooltip>
  ) : (
    <Button
      data-testid={`sdr-btn-manage-access-${dataset.id}`}
      data-attr={`datasets:btn:manage-access-${dataset.id}`}
      variant="outlined"
      color="inherit"
      size="small"
      startIcon={<PersonAddAlt1Outlined />}
      onClick={() => setIsManageAccessOpen(true)}
    >
      Manage access
    </Button>
  );

  const uploadDeniedButton = (
    <Tooltip title="You do not have permission to upload to this dataset">
      <span>
        {isMobile ? (
          <IconButton color="primary" size="small" disabled>
            <FileUploadOutlined />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            startIcon={<FileUploadOutlined />}
            disabled
          >
            Upload
          </Button>
        )}
      </span>
    </Tooltip>
  );

  const syncDeniedButton = (
    <Tooltip title="You do not have permission to sync this dataset">
      <span>
        {isMobile ? (
          <IconButton color="primary" size="small" disabled>
            <CloudSync />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            startIcon={<CloudSync />}
            disabled
          >
            Sync
          </Button>
        )}
      </span>
    </Tooltip>
  );

  const cloudImportDeniedButton = (
    <Tooltip title="You do not have permission to import to this dataset">
      <span>
        {isMobile ? (
          <IconButton size="small" disabled>
            <CloudDownload />
          </IconButton>
        ) : (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<CloudDownload />}
            disabled
          >
            Import from Cloud
          </Button>
        )}
      </span>
    </Tooltip>
  );

  const {
    mutate: syncRemoteDataset,
    isError: isSyncError,
    isSuccess: isSyncSuccess,
  } = useSyncRemoteDataset();

  const handleSyncRemoteDataset = () => {
    syncRemoteDataset(dataset.id);
  };

  const handleUploadToDataset = () => {
    history.push(`/datasets/${dataset?.id}/upload`);
  };

  /**
   * Navigate to upload page with cloud tab selected.
   * Uses query parameter to set the initial tab.
   */
  const handleNavigateToCloudImport = () => {
    history.push({
      pathname: `/datasets/${dataset?.id}/upload`,
      search: "?tab=cloud",
      state: { initialTab: 1 }, // 1 = cloud tab
    });
  };

  const { setDatasets } = useChatStore();

  const handleChatWithDataset = () => {
    // Set the dataset in the AtomAssist store
    setDatasets([
      {
        id: dataset.id,
        name: dataset.name,
        description: dataset.description,
      },
    ]);

    // Optionally set an initial message to guide the user
    // TODO: This was broken but merging to get the core feature in
    // setInitialUserMessage(
    //   `I'd like to chat about the dataset: ${dataset.name}`
    // );

    // Navigate to AtomAssist with dataset info in query params as a backup
    // This ensures the dataset selection persists even after store resets
    history.push({
      pathname: "/assistant",
      state: {
        preloadDataset: {
          id: dataset.id,
          name: dataset.name,
          description: dataset.description,
        },
      },
    });
  };

  return (
    <Box sx={{ overflow: "visible" }}>
      <Stack
        direction="row"
        gap={isMobile ? 0.25 : 1}
        flexWrap="wrap"
        justifyContent={isMobile ? "flex-end" : "flex-start"}
        alignItems="center"
        sx={{
          "& .MuiIconButton-root": {
            padding: isMobile ? "4px" : "8px",
          },
          "& .MuiButton-root": {
            textTransform: "none",
            minHeight: 32,
          },
        }}
      >
        {!overrideDatasetId && (
          <>
            <Can
              perform="dataset:update"
              yes={() =>
                isMobile ? (
                  <Tooltip title="Clear">
                    <IconButton
                      data-testid={`sdr-btn-clear-${dataset.id}`}
                      data-attr={`datasets:btn:clear-dataset-${dataset.id}`}
                      size="small"
                      onClick={() => setIsClearDatasetModalOpen(true)}
                    >
                      <ClearAllOutlined />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    data-testid={`sdr-btn-clear-${dataset.id}`}
                    data-attr={`datasets:btn:clear-dataset-${dataset.id}`}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    startIcon={<ClearAllOutlined />}
                    onClick={() => setIsClearDatasetModalOpen(true)}
                  >
                    Clear
                  </Button>
                )
              }
              no={() => (
                <Tooltip title="You do not have permission to clear this dataset">
                  <span>
                    {isMobile ? (
                      <IconButton size="small" disabled>
                        <ClearAllOutlined />
                      </IconButton>
                    ) : (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<ClearAllOutlined />}
                        disabled
                      >
                        Clear
                      </Button>
                    )}
                  </span>
                </Tooltip>
              )}
            />
            <Can
              perform="dataset:update"
              yes={() =>
                isMobile ? (
                  <Tooltip title="Edit">
                    <IconButton
                      data-testid={`sdr-btn-edit-${dataset.id}`}
                      data-attr={`datasets:btn:edit-dataset-${dataset.id}`}
                      size="small"
                      onClick={() => setisUpdateDatasetModalOpen(true)}
                    >
                      <EditOutlined />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Button
                    data-testid={`sdr-btn-edit-${dataset.id}`}
                    data-attr={`datasets:btn:edit-dataset-${dataset.id}`}
                    variant="outlined"
                    color="inherit"
                    size="small"
                    startIcon={<EditOutlined />}
                    onClick={() => setisUpdateDatasetModalOpen(true)}
                  >
                    Edit
                  </Button>
                )
              }
              no={() => (
                <Tooltip title="You do not have permission to edit this dataset">
                  <span>
                    {isMobile ? (
                      <IconButton size="small" disabled>
                        <EditOutlined />
                      </IconButton>
                    ) : (
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        startIcon={<EditOutlined />}
                        disabled
                      >
                        Edit
                      </Button>
                    )}
                  </span>
                </Tooltip>
              )}
            />
            {isOwner ? (
              manageAccessButton
            ) : (
              <Can
                perform="dataset:update"
                yes={() =>
                  canManageDataset
                    ? manageAccessButton
                    : manageAccessDeniedButton
                }
              />
            )}
            {/* Library collection flag (#2460). The toggle owns its own
                permission gate — admin capability AND per-object manage — so
                it renders nothing rather than 403ing on click. */}
            <CollectionFlagToggle
              datasetId={dataset.id}
              datasetName={dataset.name}
              isCollection={dataset.is_collection === true}
              isAdmin={Boolean(isAdmin)}
              canManage={canManageDataset}
              isMobile={isMobile}
            />
          </>
        )}
        {!hideElements?.downloadButton && (
          <Can
            perform="dataset_data:read"
            yes={() => (
              <span
                data-testid="sdr-btn-download-all"
                data-attr="datasets:btn:download-all"
              >
                <DatasetDownloadAllButton
                  datasetId={dataset.id}
                  datasetType={dataset.dataset_type}
                  datasetName={dataset.name}
                  iconOnly={isMobile}
                />
              </span>
            )}
            no={() => (
              <Tooltip title="You do not have permission to download this dataset">
                <span>
                  <DatasetDownloadAllButton
                    datasetId={dataset.id}
                    datasetType={dataset.dataset_type}
                    datasetName={dataset.name}
                    disabled
                    iconOnly={isMobile}
                  />
                </span>
              </Tooltip>
            )}
          />
        )}

        {!overrideDatasetId && (
          <Can
            perform="dataset_data:read"
            yes={() =>
              isMobile ? (
                <Tooltip title="Chat with Dataset">
                  <IconButton
                    size="small"
                    onClick={handleChatWithDataset}
                    data-testid={`ds-viewer-chat-btn-${dataset.id}`}
                    data-attr={`datasets:btn:chat-with-dataset-${dataset.id}`}
                  >
                    <ChatOutlined />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Open AtomAssist with this dataset preloaded">
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    startIcon={<ChatOutlined />}
                    onClick={handleChatWithDataset}
                    data-testid={`ds-viewer-chat-btn-${dataset.id}`}
                    data-attr={`datasets:btn:chat-with-dataset-${dataset.id}`}
                  >
                    Chat
                  </Button>
                </Tooltip>
              )
            }
            no={() => (
              <Tooltip title="You do not have permission to access this dataset">
                <span>
                  {isMobile ? (
                    <IconButton size="small" disabled>
                      <ChatOutlined />
                    </IconButton>
                  ) : (
                    <Button
                      variant="outlined"
                      color="inherit"
                      size="small"
                      startIcon={<ChatOutlined />}
                      disabled
                    >
                      Chat
                    </Button>
                  )}
                </span>
              </Tooltip>
            )}
          />
        )}

        {!overrideDatasetId && (
          <>
            {!dataset.remote_dataset ? (
              <Can
                perform="dataset_data:write"
                yes={() =>
                  !canWriteDataset ? (
                    uploadDeniedButton
                  ) : isMobile ? (
                    <Tooltip title="Upload">
                      <IconButton
                        data-testid={`sdr-btn-upload-${dataset.id}`}
                        data-attr={`datasets:btn:upload-to-dataset-${dataset.id}`}
                        color="primary"
                        size="small"
                        onClick={handleUploadToDataset}
                      >
                        <FileUploadOutlined />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Button
                      data-testid={`sdr-btn-upload-${dataset.id}`}
                      data-attr={`datasets:btn:upload-to-dataset-${dataset.id}`}
                      variant="contained"
                      color="primary"
                      size="small"
                      disableElevation
                      startIcon={<FileUploadOutlined />}
                      onClick={handleUploadToDataset}
                    >
                      Upload
                    </Button>
                  )
                }
                no={() => uploadDeniedButton}
              />
            ) : (
              <Can
                perform="dataset_data:write"
                yes={() =>
                  !canWriteDataset ? (
                    syncDeniedButton
                  ) : isMobile ? (
                    <Tooltip title="Sync">
                      <IconButton
                        data-testid={`sdr-btn-sync-${dataset.id}`}
                        data-attr={`datasets:btn:sync-dataset-${dataset.id}`}
                        color="primary"
                        size="small"
                        onClick={handleSyncRemoteDataset}
                      >
                        {isSyncSuccess ? (
                          <SyncLock />
                        ) : isSyncError ? (
                          <SyncProblem />
                        ) : (
                          <CloudSync />
                        )}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Button
                      data-testid={`sdr-btn-sync-${dataset.id}`}
                      data-attr={`datasets:btn:sync-dataset-${dataset.id}`}
                      variant="contained"
                      color="primary"
                      size="small"
                      disableElevation
                      startIcon={
                        isSyncSuccess ? (
                          <SyncLock />
                        ) : isSyncError ? (
                          <SyncProblem />
                        ) : (
                          <CloudSync />
                        )
                      }
                      onClick={handleSyncRemoteDataset}
                    >
                      Sync
                    </Button>
                  )
                }
                no={() => syncDeniedButton}
              />
            )}
          </>
        )}

        {/* Import from Cloud button - only show for non-remote datasets */}
        {/* Requires both dataset_data:write AND cloud:use permissions (backend enforces both) */}
        {!overrideDatasetId && !dataset.remote_dataset && (
          <Can
            perform="dataset_data:write"
            yes={() =>
              !canWriteDataset ? (
                cloudImportDeniedButton
              ) : (
                <Can
                  perform="cloud:use"
                  yes={() =>
                    isMobile ? (
                      <Tooltip title="Import from Cloud">
                        <IconButton
                          size="small"
                          onClick={handleNavigateToCloudImport}
                        >
                          <CloudDownload />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Import files from cloud storage (SharePoint, FileNet, CMIS)">
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<CloudDownload />}
                          onClick={handleNavigateToCloudImport}
                        >
                          Import from Cloud
                        </Button>
                      </Tooltip>
                    )
                  }
                  no={() => (
                    <Tooltip title="You do not have permission to use cloud import features">
                      <span>
                        {isMobile ? (
                          <IconButton size="small" disabled>
                            <CloudDownload />
                          </IconButton>
                        ) : (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<CloudDownload />}
                            disabled
                          >
                            Import from Cloud
                          </Button>
                        )}
                      </span>
                    </Tooltip>
                  )}
                />
              )
            }
            no={() => cloudImportDeniedButton}
          />
        )}
      </Stack>

      {isUpdateDatasetModalOpen && (
        <UpdateDatasetModal
          id={dataset.id}
          isVisible={isUpdateDatasetModalOpen}
          onClose={() => setisUpdateDatasetModalOpen(false)}
        />
      )}
      {isClearDatasetModalOpen && (
        <ClearDatasetModal
          id={dataset.id}
          isVisible={isClearDatasetModalOpen}
          datasetName={dataset.name}
          onClose={() => setIsClearDatasetModalOpen(false)}
        />
      )}
      {isManageAccessOpen && (
        <ShareDialog
          open={isManageAccessOpen}
          resource={{ type: "dataset", id: dataset.id, name: dataset.name }}
          canTransferOwnership={isOwner || isAdmin}
          onClose={() => setIsManageAccessOpen(false)}
        />
      )}
    </Box>
  );
};

export default DatasetHeaderActions;
